// src/components/sidebar/Sidebar.tsx
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Drawer,
  Box,
  Typography,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { ConversationList } from "./ConversationList";
import { UserMenu } from "./UserMenu";
import { Logo } from "../Logo";
import { useConversationStore } from "../../stores/conversationStore";
import { useAuthStore } from "../../stores/authStore";
import { useT } from '../../i18n/useT';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  onNewConversation: (conversationId: string) => void;
  onSelectConversation: (conversationId: string) => void;
  currentConversationId: string | null;
}

const SIDEBAR_WIDTH = 280;

export const Sidebar: React.FC<SidebarProps> = ({
  open,
  onClose,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onNewConversation,
  onSelectConversation,
  currentConversationId,
}) => {
  const t = useT();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { isAuthenticated, authMode, openAuthModal } = useAuthStore();
  const {
    startConversationListener,
    conversations,
    isCreating,
  } = useConversationStore();

  // Load conversations when user is authenticated
  useEffect(() => {
    if (isAuthenticated && authMode === "registered") {
      startConversationListener();
    }
  }, [isAuthenticated, authMode, startConversationListener]);

  const currentConversation = currentConversationId
    ? conversations.find((c) => c.id === currentConversationId)
    : null;
  const isCurrentConversationEmpty =
    currentConversation && currentConversation.message_count === 0;

  // "New Chat" no longer pre-creates a conversation. Pre-creating
  // produces an empty Firestore doc the user has to commit to before
  // they ever get to pick a mode on the WelcomeScreen — and the
  // WelcomeScreen guards on `!conversationId`, so a pre-created chat
  // would skip the picker entirely. Instead, route the user to
  // /chat with no id and a freshChat state flag. The state flag tells
  // ChatRoute to skip its auto-resume effect (which would otherwise
  // bounce them straight back to their last conversation). The
  // WelcomeScreen renders, the user picks a mode + types, and
  // useChat lazily creates the conversation with the right type +
  // mode subdoc on send.
  const handleNewConversation = () => {
    if (isCurrentConversationEmpty) {
      return;
    }
    navigate("/chat", { state: { freshChat: true } });
    if (isMobile) {
      onClose();
    }
  };

  const handleSelectConversation = (conversationId: string) => {
    console.log("Selecting conversation:", conversationId);
    onSelectConversation(conversationId);
    if (isMobile) {
      onClose();
    }
  };

  const sidebarContent = (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
        <Box sx={{ mb: 2 }}>
          <Logo size={24} alt={t("Instant Tours")} />
        </Box>

        <Button
          variant="outlined"
          fullWidth
          startIcon={<Add />}
          onClick={handleNewConversation}
          disabled={isCurrentConversationEmpty || isCreating}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            py: 1,
          }}
        >
          
          {t("New Conversation")}
        </Button>
      </Box>

      {/* Conversation List */}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        {authMode === "registered" ? (
          <ConversationList
            conversations={conversations}
            currentConversationId={currentConversationId}
            onSelectConversation={handleSelectConversation}
          />
        ) : (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              
              {t("Sign up to save and sync your conversations across devices")}
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => openAuthModal("signup")}
              sx={{ mb: 1, textTransform: "none" }}
            >
              
              {t("Create Account")}
            </Button>
            <Box>
              <Button
                variant="text"
                size="small"
                onClick={() => openAuthModal("login")}
                sx={{ textTransform: "none" }}
              >
                
                {t("Already have an account? Sign in")}
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Bottom Section */}
      <Box sx={{ mt: "auto" }}>
        <Divider />
        <UserMenu />
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: SIDEBAR_WIDTH,
            borderRadius: 0,
          },
        }}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={open}
      PaperProps={{
        sx: {
          width: SIDEBAR_WIDTH,
          borderRight: 1,
          borderColor: "divider",
          position: "relative",
          height: "100dvh",
          "@supports not (height: 100dvh)": { height: "100vh" },
        },
      }}
    >
      {sidebarContent}
    </Drawer>
  );
};
